import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  viewChildren,
  ChangeDetectionStrategy,
  linkedSignal,
  effect,
} from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { Row, Table } from 'src/app/models/ui/table';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { SpinnerService } from 'src/app/services/Spinner/spinner.service';
import { ColumnFilterComponent } from '../../filter/column-filter/column-filter.component';
import { FILTER_TABLE_STORE, provideFilterStore } from 'src/app/shared/stores/filter-table.store';
import { getTokenRight } from 'src/app/misc/functions';
import { IconDgCellComponent } from '../../_shared/datagrid-cell/icon-dg-cell.component';
import { ConfirmService } from 'src/app/services/confirm.service';
import { AllSelectedBtnComponent } from '../../_shared/all-selected-btn/all-selected-btn.component';
import { FormsModule } from '@angular/forms';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { PrüfungsTypStore } from 'src/app/services/Shared/prüfung-typ.store';

@Component({
  selector: 'app-sammelexportdialog',
  templateUrl: './sammelexportdialog.component.html',
  styleUrls: ['./sammelexportdialog.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ColumnFilterComponent,
    IconDgCellComponent,
    AllSelectedBtnComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
  ],
  providers: [provideFilterStore({ storage: 'sammelexport' })],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SammelexportsDialogComponent implements OnInit {
  private readonly apiservice = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly overlayService = inject(OverlayService);
  private readonly basicStore = inject(BasicStoreService);
  private readonly spinner = inject(SpinnerService);
  private readonly KPRUVID = inject(PrüfungsTypStore).KPRUVID();
  private readonly confirmService = inject(ConfirmService);

  protected readonly authService = inject(AuthService);
  protected readonly filterStore = inject(FILTER_TABLE_STORE);

  private readonly columnFilters = viewChildren(ColumnFilterComponent);

  protected readonly table = signal<Table>(undefined);
  //protected readonly selected = linkedSignal<Row[]>(() => (this.table(), []));

  protected readonly selected = linkedSignal<Table<Row>, Row[]>({
    source: () => this.table(),
    computation: tableObject => tableObject?.rows ?? [],
  });

  protected readonly filteredRows = this.filterStore.filteredRows;

  public readonly closedialog = new BehaviorSubject(false);

  protected readonly progVersion: number = 50;
  private readonly rights: any;

  protected readonly isCheckedAll = signal(false);
  protected readonly trackId = computed(() => (this.isCheckedAll() ? 'LPID' : 'OSTAMMID'));

  protected readonly PRDEL = computed(
    () => this.selected().length == 1 && getTokenRight(this.rights.type[+this.selected()[0].OTYPID], 'PRDEL')
  );
  protected readonly STDEL = computed(() =>
    this.selected().every(x => getTokenRight(this.rights.type[+x.OTYPID], 'STDEL'))
  );

  protected readonly promptEvent = signal<Event>(undefined);

  set data(dataIn: any) {
    this.table.set(dataIn.table);
  }

  constructor() {
    this.rights = JSON.parse(this.authService.decrypt(localStorage.getItem('rights')));

    const token = this.authService.getToken();
    if (token && token.rights) {
      this.progVersion = parseInt(token.rights.progVersion);
    }

    this.filterStore.registerTable(this.table);
    this.filterStore.registerFilters(this.columnFilters);

    effect(async () => {
      if (this.isCheckedAll()) {
        this.spinner.enable();
        const anlagen = await firstValueFrom(this.apiservice.getAnlagen(this.KPRUVID, { all: true }));
        this.table.set(anlagen);
        this.spinner.disable();
      } else {
        this.table.set(this.basicStore.getComponentStore('anlagen').anlagen ?? TABLE_EMPTY());
      }
    });
  }

  ngOnInit(): void {
    window.addEventListener('beforeinstallprompt', event => {
      event.preventDefault();
      this.promptEvent.set(event);
    });
  }

  private async reloadAnlagen() {
    let anlagen = await firstValueFrom(this.apiservice.getAnlagen(this.KPRUVID));
    this.basicStore.resetComponentStore('anlagen', { anlagen });
    if (this.isCheckedAll()) anlagen = await firstValueFrom(this.apiservice.getAnlagen(this.KPRUVID, { all: true }));

    this.table.set(anlagen);
  }

  async stammdel() {
    const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie die ausgewählten Objekte löschen wollen?');
    const ok = await this.confirmService.danger(text);
    if (!ok) return;

    const OSTAMMIDs = this.selected().map(x => +x.OSTAMMID);
    const { success } = await firstValueFrom(this.apiservice.deleteStammDaten(OSTAMMIDs));
    if (success) this.reloadAnlagen();
  }

  async stammarchiv() {
    const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie die ausgewählten Objekte archivieren wollen?');
    const ok = await this.confirmService.warning(text);
    if (!ok) return;

    const OSTAMMIDs = this.selected().map(x => +x.OSTAMMID);
    const { success } = await firstValueFrom(this.apiservice.archivStammDaten(OSTAMMIDs));
    if (success) this.reloadAnlagen();
  }

  async delpruef() {
    const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie die ausgewählte Prüfung löschen wollen?');
    const ok = await this.confirmService.warning(text);
    if (!ok) return;

    const row = this.selected()[0];
    const sendObj = {
      OSTAMMID: +row.OSTAMMID,
      LPID: +row.LPID,
      kprtypid: this.KPRUVID,
    };

    const { success } = await firstValueFrom(this.apiservice.deletepruefungStammDaten(sendObj));
    if (success) this.reloadAnlagen();
  }

  async createMessdatenExport() {
    this.spinner.enable();
    try {
      this.selected.update(currentRows => {
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        return [...currentRows].sort((a, b) => {
          const valA = a.bezeichnung ? String(a.bezeichnung) : '';
          const valB = b.bezeichnung ? String(b.bezeichnung) : '';
          return collator.compare(valA, valB);
        });
      });

      const OSTAMMIDs = this.selected()
        .filter(x => x.OSTAMMID)
        .map(x => +x.OSTAMMID)
        .join(',');

      const data = await firstValueFrom(this.apiservice.getMessdatenExport(OSTAMMIDs));

      if (data) {
        const now = new Date();

        const filename = `MR_Auftrag_${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now
          .getDate()
          .toString()
          .padStart(2, '0')}-${now.getHours().toString().padStart(2, '0')}-${now
          .getMinutes()
          .toString()
          .padStart(2, '0')}-${now.getSeconds().toString().padStart(2, '0')}.xml`;

        const url = window.URL.createObjectURL(data);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;

        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.spinner.disable();
    }
  }

  async createSammel() {
    this.spinner.enable();
    try {
      const OPRUEFIDs = this.selected()
        .filter(x => x.LPID)
        .map(x => +x.LPID)
        .join(',');

      const data = await firstValueFrom(this.apiservice.getPDFFile(OPRUEFIDs));

      if (data) {
        this.overlayService.setOverlay({
          overlay: 'viewAuftrag',
          data,
          zIndex: 10,
          isTopOverlay: true,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.spinner.disable();
    }
  }

  protected close() {
    this.closedialog.next(true);
  }
}
